import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import {
  getTeam,
  inviteTeamMember,
  listTeamListings,
  removeCohostListings,
  revokeTeamInvitation,
  setCohostListings,
  TeamInvitation,
  TeamListing,
  TeamMember,
  TeamResponse,
  TeamRole,
} from '../../../../api/stays-team-client';
import { listStaysBuildings, StaysApiError } from '../../../../api/stays-client';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { INVITABLE_ROLES, normalizeRole, roleLabel, ROLES } from '../../../../auth/roles';
import { staysOnlyAccount } from '../../../../utils/products';
import { StateSwitch } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { toast } from '../../../../utils/toast';
import { StaysSectionTabs } from '../stays-section';
import { Quiet, StepTitle, Why } from '../setup/setup-wizard.styled';
import {
  Actions,
  CardList,
  CardMeta,
  CardTitle,
  Inline,
  Page,
  PageHead,
  PropertyCard,
  Section,
  SectionTitle,
  Small,
  Statement,
  UnitList,
  UnitRow,
} from '../properties/properties.styled';

/* The two roles a Stays team is made of (plan C-3): a co-host works on the
   listings shared with them; a property manager looks after whole properties. */
const TEAM_ROLES: readonly TeamRole[] = [ROLES.STAYS_COHOST, ROLES.PROPERTY_MANAGER];

const ROLE_HINT: Record<TeamRole, string> = {
  stays_cohost: 'Works on the listings you share: stays, check-ins, guests and messages. Never billing, the team or amounts.',
  property_manager: 'Looks after the properties you choose, with the same Stays rights as you there, except billing.',
};

/* IS5.4 (plan C-5): inviting someone completes the setup guide's optional
   "Invite your team" step. Only a Stays-only owner runs that guide; a refusal is
   ignored — the step is a nicety, never a gate. */
const markTeamStep = (isStaysHost: boolean) => {
  if (!isStaysHost) return;
  axios.patch(`${apiRoute}/onboarding/step`, { step: 'team' }).catch(() => undefined);
};

/**
 * YOUR TEAM — /dashboard/stays/team (Independent Stays IS5).
 * The organisation's co-hosts and property managers, the invitations still
 * open, "Invite someone", and per co-host: which listings they work on.
 * Removing a co-host's last listing leaves their account with an empty view
 * (R-IS5.d); switching the account off is People → the person.
 */
export const TeamPage: React.FC = () => {
  const { permissions, user } = useAuth();
  const actor = normalizeRole(user?.role);
  const canChange = can(permissions, PERMISSIONS.STAYS_MANAGE) && can(permissions, PERMISSIONS.INVITE_CREATE);
  const invitable = TEAM_ROLES.filter(r => (actor ? INVITABLE_ROLES[actor] || [] : []).includes(r));
  const isStaysHost = actor === ROLES.OWNER && staysOnlyAccount(user);
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [listings, setListings] = useState<TeamListing[]>([]);
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [cancelling, setCancelling] = useState<TeamInvitation | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback((quiet = false) => {
    if (!quiet) setView('loading');
    Promise.all([getTeam(), listTeamListings().catch(() => ({ items: [] as TeamListing[] }))])
      .then(([t, l]) => {
        setTeam(t);
        setListings(l.items);
        setView('ready');
      })
      .catch(() => !quiet && setView('error'));
  }, []);
  useEffect(() => load(), [load]);

  const nameOf = useMemo(() => {
    const m = new Map(listings.map(l => [l.listing_id, l.display_name]));

    return (id: string) => m.get(id) || 'A listing';
  }, [listings]);

  const doRemove = async () => {
    if (!removing) return;
    setProblem(null);
    try {
      await removeCohostListings(removing.user_id);
      toast.success(`${removing.name || removing.email} no longer works on any listing.`);
      setRemoving(null);
      load(true);
    } catch (e) {
      setProblem(refusalText(e instanceof StaysApiError ? e : null, "Could not remove the co-host's listings. Please try again."));
    }
  };

  const doCancel = async () => {
    if (!cancelling) return;
    setProblem(null);
    try {
      await revokeTeamInvitation(cancelling.id);
      toast.success(`The invitation to ${cancelling.email} was cancelled.`);
      setCancelling(null);
      load(true);
    } catch (e) {
      setProblem(refusalText(e instanceof StaysApiError ? e : null, 'Could not cancel the invitation. Please try again.'));
    }
  };

  const members = team ? team.members : [];
  const invitations = team ? team.invitations : [];

  return (
    <Page data-team-page="">
      <StaysSectionTabs />
      <PageHead>
        <div>
          <StepTitle as="h2">Your team</StepTitle>
          <Why>Co-hosts work on the listings you share with them. Property managers look after whole properties.</Why>
        </div>
        {canChange && invitable.length ? <AppButton onClick={() => setInviting(true)}>Invite someone</AppButton> : null}
      </PageHead>
      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={3} label="your team" />
        ) : view === 'error' ? (
          <LoadError message="Could not load your team." onRetry={() => load()} />
        ) : (
          <>
            {members.length === 0 && invitations.length === 0 ? (
              <Quiet>Nobody else is on your team yet.</Quiet>
            ) : null}
            {members.length ? (
              <CardList aria-label="Your team">
                {members.map(m => (
                  <PropertyCard key={m.user_id} data-team-member={m.email}>
                    <div>
                      <CardTitle>{m.name || m.email}</CardTitle>
                      <CardMeta>
                        {m.email} · {roleLabel(m.role)}
                        {m.status !== 'active' ? ' · switched off' : ''}
                      </CardMeta>
                    </div>
                    {m.role === ROLES.STAYS_COHOST ? (
                      m.listing_ids.length ? (
                        <UnitList aria-label={`Listings ${m.name || m.email} works on`}>
                          {m.listing_ids.map(id => (
                            <UnitRow key={id}>
                              <span>{nameOf(id)}</span>
                            </UnitRow>
                          ))}
                        </UnitList>
                      ) : (
                        <Small>No listings shared. They can sign in and see nothing until you share one.</Small>
                      )
                    ) : null}
                    {canChange && m.role === ROLES.STAYS_COHOST ? (
                      <Actions>
                        <AppButton
                          variant="secondary"
                          size="compact"
                          onClick={() => setEditing(m)}
                          aria-label={`Change the listings of ${m.name || m.email}`}
                        >
                          Change listings
                        </AppButton>
                        {m.listing_ids.length ? (
                          <AppButton
                            variant="ghost"
                            size="compact"
                            onClick={() => {
                              setProblem(null);
                              setRemoving(m);
                            }}
                            aria-label={`Remove every listing of ${m.name || m.email}`}
                          >
                            Remove all listings
                          </AppButton>
                        ) : null}
                      </Actions>
                    ) : null}
                  </PropertyCard>
                ))}
              </CardList>
            ) : null}
            {invitations.length ? (
              <CardList aria-label="Invitations waiting">
                {invitations.map(i => (
                  <PropertyCard key={i.id} data-team-invitation={i.email}>
                    <div>
                      <CardTitle>{i.email}</CardTitle>
                      <CardMeta>
                        <StatusPill tone="warning" dot>
                          Invited
                        </StatusPill>{' '}
                        {roleLabel(i.role)}
                        {i.role === ROLES.STAYS_COHOST && i.listing_ids.length
                          ? ` · ${i.listing_ids.map(nameOf).join(', ')}`
                          : ''}
                      </CardMeta>
                    </div>
                    {canChange ? (
                      <Actions>
                        <AppButton
                          variant="ghost"
                          size="compact"
                          onClick={() => {
                            setProblem(null);
                            setCancelling(i);
                          }}
                          aria-label={`Cancel the invitation to ${i.email}`}
                        >
                          Cancel invitation
                        </AppButton>
                      </Actions>
                    ) : null}
                  </PropertyCard>
                ))}
              </CardList>
            ) : null}
          </>
        )}
      </StateSwitch>

      <InviteModal
        open={inviting}
        roles={invitable}
        listings={listings}
        onClose={() => setInviting(false)}
        onSent={email => {
          setInviting(false);
          toast.success(`An invitation was sent to ${email}.`);
          markTeamStep(isStaysHost);
          load(true);
        }}
      />
      <ListingsModal
        member={editing}
        listings={listings}
        onClose={() => setEditing(null)}
        onSaved={saved => {
          // The reply's member at once, so a "Change listings" opened before the
          // refresh lands never starts from the listings as they were.
          if (saved) setTeam(t => (t ? { ...t, members: t.members.map(x => (x.user_id === saved.user_id ? saved : x)) } : t));
          setEditing(null);
          load(true);
        }}
      />
      <ConfirmDialog
        open={!!removing}
        title={removing ? `Remove every listing of ${removing.name || removing.email}?` : 'Remove every listing?'}
        message="They keep their account and can sign in, but see no listing until you share one again."
        primaryLabel="Confirm: remove all listings"
        primaryVariant="destructive"
        onPrimary={doRemove}
        dismissLabel="Keep them"
        onDismiss={() => setRemoving(null)}
      >
        {problem ? <FieldError id="team-remove-problem" message={problem} /> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={!!cancelling}
        title={cancelling ? `Cancel the invitation to ${cancelling.email}?` : 'Cancel the invitation?'}
        message="The link in their e-mail stops working."
        primaryLabel="Confirm: cancel invitation"
        primaryVariant="destructive"
        onPrimary={doCancel}
        dismissLabel="Keep it"
        onDismiss={() => setCancelling(null)}
      >
        {problem ? <FieldError id="team-cancel-problem" message={problem} /> : null}
      </ConfirmDialog>
    </Page>
  );
};

/* ── The listings checkboxes (invite and change) ── */

const ListingChoices: React.FC<{
  idPrefix: string;
  listings: TeamListing[];
  chosen: Set<string>;
  onToggle: (id: string) => void;
}> = ({ idPrefix, listings, chosen, onToggle }) =>
  listings.length ? (
    <Section>
      <SectionTitle>Listings they work on</SectionTitle>
      {listings.map(l => (
        <Statement key={l.listing_id} htmlFor={`${idPrefix}-${l.listing_id}`}>
          <input
            id={`${idPrefix}-${l.listing_id}`}
            type="checkbox"
            checked={chosen.has(l.listing_id)}
            onChange={() => onToggle(l.listing_id)}
          />
          <span>
            {l.display_name}
            {l.building_name && l.building_name !== l.display_name ? ` · ${l.building_name}` : ''}
          </span>
        </Statement>
      ))}
    </Section>
  ) : (
    <Small>You have no live listing to share yet.</Small>
  );

const toggleIn = (set: Set<string>, id: string): Set<string> => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);

  return next;
};

/* ── Change a co-host's listings ── */

const ListingsModal: React.FC<{
  member: TeamMember | null;
  listings: TeamListing[];
  onClose: () => void;
  onSaved: (saved: TeamMember | null) => void;
}> = ({ member, listings, onClose, onSaved }) => {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setChosen(new Set(member ? member.listing_ids : []));
    setProblem(null);
  }, [member]);

  const save = async () => {
    if (!member) return;
    setSaving(true);
    setProblem(null);
    try {
      let saved: TeamMember | null;
      if (chosen.size) saved = (await setCohostListings(member.user_id, [...chosen])).member;
      else {
        await removeCohostListings(member.user_id);
        saved = { ...member, listing_ids: [] };
      }
      toast.success(`${member.name || member.email}'s listings were saved.`);
      onSaved(saved);
    } catch (e) {
      setProblem(refusalText(e instanceof StaysApiError ? e : null, "Could not save the co-host's listings. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={!!member}
      title={member ? `Listings of ${member.name || member.email}` : 'Listings'}
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={save} loading={saving} loadingLabel="Saving…">
            Save
          </AppButton>
        </Inline>
      }
    >
      <ListingChoices idPrefix="team-edit" listings={listings} chosen={chosen} onToggle={id => setChosen(s => toggleIn(s, id))} />
      <FieldHint>A listing you untick disappears from their view at once.</FieldHint>
      {problem ? <FieldError id="team-edit-problem" message={problem} /> : null}
    </FormModal>
  );
};

/* ── Invite a co-host or a property manager ── */

interface Building {
  building_id: number;
  name: string;
}

const InviteModal: React.FC<{
  open: boolean;
  roles: readonly TeamRole[];
  listings: TeamListing[];
  onClose: () => void;
  onSent: (email: string) => void;
}> = ({ open, roles, listings, onClose, onSent }) => {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>(roles[0] || ROLES.STAYS_COHOST);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [places, setPlaces] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirst('');
    setLast('');
    setEmail('');
    setRole(roles[0] || ROLES.STAYS_COHOST);
    setChosen(new Set());
    setPlaces(new Set());
    setErrors({});
    setProblem(null);
    listStaysBuildings()
      .then(r => setBuildings(r.items.map(b => ({ building_id: b.building_id, name: b.name }))))
      .catch(() => setBuildings([]));
  }, [open, roles]);

  const send = async () => {
    const e: Record<string, string> = {};
    if (!first.trim()) e.first_name = 'Enter their first name.';
    if (!last.trim()) e.last_name = 'Enter their last name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) e.email = 'Enter a valid e-mail address.';
    if (role === ROLES.STAYS_COHOST && !chosen.size) e.stays_listing_ids = 'Choose at least one listing.';
    if (role === ROLES.PROPERTY_MANAGER && !places.size) e.property_ids = 'Choose at least one property.';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    setProblem(null);
    try {
      await inviteTeamMember({
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
        role,
        ...(role === ROLES.STAYS_COHOST
          ? { stays_listing_ids: [...chosen] }
          : { property_ids: [...places].map(Number) }),
      });
      onSent(email.trim());
    } catch (err) {
      const se = err instanceof StaysApiError ? err : null;
      if (se && se.field === 'stays_listing_ids') setErrors({ stays_listing_ids: se.message });
      else setProblem(refusalText(se, 'Could not send the invitation. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={open}
      title="Invite someone to your team"
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={send} loading={saving} loadingLabel="Sending…">
            Send invitation
          </AppButton>
        </Inline>
      }
    >
      <FieldRow>
        <Field>
          <Label htmlFor="team-invite-first">First name</Label>
          <Input id="team-invite-first" value={first} maxLength={80} onChange={ev => setFirst(ev.target.value)} />
          <FieldError id="team-invite-first-error" message={errors.first_name} />
        </Field>
        <Field>
          <Label htmlFor="team-invite-last">Last name</Label>
          <Input id="team-invite-last" value={last} maxLength={80} onChange={ev => setLast(ev.target.value)} />
          <FieldError id="team-invite-last-error" message={errors.last_name} />
        </Field>
      </FieldRow>
      <Field>
        <Label htmlFor="team-invite-email">E-mail</Label>
        <Input id="team-invite-email" type="email" value={email} maxLength={254} onChange={ev => setEmail(ev.target.value)} />
        <FieldError id="team-invite-email-error" message={errors.email} />
      </Field>
      {roles.length > 1 ? (
        <Field>
          <Label htmlFor="team-invite-role">Role</Label>
          <Select id="team-invite-role" value={role} onChange={ev => setRole(ev.target.value as TeamRole)}>
            {roles.map(r => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <FieldHint>{ROLE_HINT[role]}</FieldHint>
      {role === ROLES.STAYS_COHOST ? (
        <>
          <ListingChoices idPrefix="team-invite" listings={listings} chosen={chosen} onToggle={id => setChosen(s => toggleIn(s, id))} />
          <FieldError id="team-invite-listings-error" message={errors.stays_listing_ids} />
        </>
      ) : (
        <>
          <Section>
            <SectionTitle>Properties they look after</SectionTitle>
            {buildings.map(b => (
              <Statement key={b.building_id} htmlFor={`team-invite-b-${b.building_id}`}>
                <input
                  id={`team-invite-b-${b.building_id}`}
                  type="checkbox"
                  checked={places.has(String(b.building_id))}
                  onChange={() => setPlaces(s => toggleIn(s, String(b.building_id)))}
                />
                <span>{b.name}</span>
              </Statement>
            ))}
          </Section>
          <FieldError id="team-invite-places-error" message={errors.property_ids} />
        </>
      )}
      {problem ? <FieldError id="team-invite-problem" message={problem} /> : null}
    </FormModal>
  );
};
